import { useState, useEffect } from "react";
import { ArrowRight, ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { ContactDialog } from "@/components/ContactDialog";
import { useLang } from "@/context/LanguageContext";
import { useReveal } from "@/hooks/useReveal";

// ---------- Product card brand config ----------
const BRAND = {
  "DóRéMigos": {
    bg: "#FFF8E1",
    accent: "#E91E8C",
    text: "#1a1a1a",
    textMuted: "#1a1a1a99",
    url: "https://doremigos.com.br",
    displayUrl: "DOREMIGOS.COM.BR",
    icon: "/products/doremigos-logo.png",
    screenshot: "/products/doremigos-tela.png",
    badge: "BRANDING",
    badgeColor: "#E91E8C",
    number: "01",
    tag: "MARCA · INFANTIL",
  },
  PinWin: {
    bg: "#0a0a0a",
    accent: "#B6FF1A",
    text: "#ffffff",
    textMuted: "#ffffff99",
    url: "https://pinwin.social",
    displayUrl: "PINWIN.SOCIAL",
    icon: "/products/pinwin-logo-white.png",
    screenshot: "/products/pinwin-tela.jpg",
    badge: "BETA",
    badgeColor: "#B6FF1A",
    number: "02",
    tag: "SAAS",
  },
  Nico: {
    bg: "#F5EDDA",
    accent: "#2D7A3A",
    text: "#1a2e1a",
    textMuted: "#1a2e1a99",
    url: "https://usenico.com.br",
    displayUrl: "USENICO.COM.BR",
    icon: "/products/nico-logo.png",
    screenshot: "/products/nico-tela.png",
    badge: "LAB",
    badgeColor: "#2D7A3A",
    number: "03",
    tag: "ASSISTENTE",
  },
};

const BOOK_IMG =
  "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=800&q=80&auto=format&fit=crop";

// ---------- Header ----------
const Header = ({ onOpenContact }) => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { key: "products", label: t.nav.products, href: "#products" },
    { key: "editorial", label: t.nav.editorial, href: "#editorial" },
    { key: "services", label: t.nav.services, href: "#services" },
    { key: "contact", label: t.nav.contact, onClick: onOpenContact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--lab-cream)]/85 backdrop-blur-md border-b border-[var(--lab-line)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4 md:py-5">
        <a href="#top" className="flex items-center" data-testid="header-logo">
          <Logo className="h-20 md:h-32 w-auto" />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.onClick ? (
              <button
                key={l.key}
                onClick={l.onClick}
                data-testid={`nav-${l.key}-btn`}
                className="font-mono-label text-[var(--lab-ink)] hover:text-[var(--lab-orange)] transition-colors link-underline"
              >
                {l.label}
              </button>
            ) : (
              <a
                key={l.key}
                href={l.href}
                data-testid={`nav-${l.key}-link`}
                className="font-mono-label text-[var(--lab-ink)] hover:text-[var(--lab-orange)] transition-colors link-underline"
              >
                {l.label}
              </a>
            )
          )}
          <LanguageSwitch />
        </nav>

        {/* Mobile trigger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-[var(--lab-ink)]"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[80] bg-[var(--lab-ink)] text-[var(--lab-cream)] md:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex justify-between items-center px-6 py-4">
            <Logo className="h-14 w-auto brightness-0 invert" />
            <button
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 flex items-center justify-center"
              data-testid="mobile-close-btn"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>
          <nav className="flex flex-col gap-2 px-6 pt-16">
            {links.map((l) =>
              l.onClick ? (
                <button
                  key={l.key}
                  onClick={() => {
                    setMobileOpen(false);
                    l.onClick();
                  }}
                  className="font-display text-4xl text-left py-3 hover:text-[var(--lab-orange)] transition-colors"
                >
                  {l.label}
                </button>
              ) : (
                <a
                  key={l.key}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="font-display text-4xl py-3 hover:text-[var(--lab-orange)] transition-colors"
                >
                  {l.label}
                </a>
              )
            )}
            <div className="mt-8">
              <LanguageSwitch variant="dark" />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

// ---------- Hero ----------
const Hero = ({ onOpenContact }) => {
  const { t } = useLang();
  const ref = useReveal();

  return (
    <section
      id="top"
      className="relative pt-40 md:pt-48 pb-24 md:pb-32 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="grain-overlay opacity-40" />
      <div ref={ref} className="reveal max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="font-mono-label text-[var(--lab-orange)] mb-8" data-testid="hero-tag">
          {t.hero.tag}
        </div>

        <h1 className="font-display hero-title text-[clamp(8rem,12vw,10rem)] leading-[0.9] tracking-tight text-[var(--lab-ink)]">
          <span className="inline-block">
            {t.hero.title1.split("&")[0]}
            <span className="text-[var(--lab-orange)]">&</span>
          </span>
          <br />
          <span className="inline-block">
            {t.hero.title2}
            <span className="font-serif-italic text-[var(--lab-ink)]/25 ml-4 md:ml-8">
              {t.hero.titleItalic}
            </span>
          </span>
        </h1>

        <div className="mt-16 md:mt-20 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-1 hidden md:block">
            <div className="font-mono-label text-[var(--lab-ink)]/40">↓</div>
          </div>
          <p className="md:col-span-6 text-lg md:text-xl text-[var(--lab-ink)]/80 leading-relaxed max-w-xl">
            {t.hero.description}
          </p>
          <div className="md:col-span-5 flex md:justify-end items-start">
            <button
              onClick={onOpenContact}
              data-testid="hero-cta-btn"
              className="btn-pill bg-[var(--lab-ink)] text-[var(--lab-cream)] hover:bg-[var(--lab-orange)] group"
            >
              {t.hero.cta}
              <ArrowRight size={16} className="arrow-anim" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- Products ----------
const Products = ({ onOpenContact }) => {
  const { t } = useLang();
  const ref = useReveal();

  return (
    <section
      id="products"
      className="relative bg-[var(--lab-ink)] text-[var(--lab-cream)] py-24 md:py-32 overflow-hidden"
      data-testid="products-section"
    >
      <div className="absolute inset-0 pattern-dots opacity-40" />
      <div
        ref={ref}
        className="reveal max-w-[1400px] mx-auto px-6 md:px-10 relative"
      >
        <div className="font-mono-label text-[var(--lab-orange)] mb-6">
          {t.products.tag}
        </div>

        <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
          <h2 className="md:col-span-8 font-display text-5xl md:text-7xl leading-[0.95] text-[var(--lab-cream)]">
            {t.products.title}
          </h2>
          <p className="md:col-span-4 text-[var(--lab-cream)]/70 text-base leading-relaxed">
            {t.products.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {t.products.cards.map((card, idx) => (
            <ProductCard key={idx} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ProductCard = ({ card }) => {
  const brand = BRAND[card.name];

  return (
    <div
      className="group relative rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-1"
      style={{ backgroundColor: brand.bg }}
      data-testid={`product-card-${card.name.toLowerCase().replace(/[^a-z]/g, "")}`}
    >
      <div className="p-6 md:p-8 flex flex-col h-full min-h-[520px]">
        {/* Top bar: icon + badge */}
        <div className="flex items-center justify-between mb-6">
          <img
            src={brand.icon}
            alt={card.name}
            className="h-8 w-auto object-contain"
          />
          <span
            className="font-mono-label text-[0.65rem] tracking-widest px-3 py-1 rounded-full border"
            style={{
              color: brand.badgeColor,
              borderColor: brand.badgeColor,
            }}
          >
            {brand.badge}
          </span>
        </div>

        {/* Number + tag */}
        <div
          className="font-mono-label text-xs mb-3 tracking-wider"
          style={{ color: brand.textMuted }}
        >
          # {brand.number} · {brand.tag}
        </div>

        {/* Product name */}
        <h3
          className="font-display text-4xl md:text-5xl mb-4 leading-[0.95]"
          style={{ color: brand.text }}
        >
          {card.name}
        </h3>

        {/* Description */}
        <p
          className="text-sm leading-relaxed mb-6"
          style={{ color: brand.textMuted }}
        >
          {card.description}
        </p>

        {/* App screenshot */}
        <div className="flex-1 -mx-6 md:-mx-8 overflow-hidden flex items-end">
          <img
            src={brand.screenshot}
            alt={`${card.name} app`}
            className="w-full object-cover object-top rounded-t-lg opacity-80 group-hover:opacity-100 transition-opacity duration-500"
            style={{ maxHeight: "200px" }}
          />
        </div>

        {/* Bottom bar: URL + VER link */}
        <div className="flex items-center justify-between pt-5 mt-auto">
          <span
            className="font-mono-label text-[0.65rem] tracking-widest"
            style={{ color: brand.textMuted }}
          >
            {brand.displayUrl}
          </span>
          <a
            href={brand.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`product-cta-${card.name.toLowerCase().replace(/[^a-z]/g, "")}`}
            className="font-mono-label text-xs tracking-wider inline-flex items-center gap-2 transition-opacity hover:opacity-70"
            style={{ color: brand.text }}
          >
            VER
            <ArrowRight size={12} />
          </a>
        </div>
      </div>
    </div>
  );
};

// ---------- Editorial ----------
const Editorial = () => {
  const { t } = useLang();
  const ref = useReveal();

  return (
    <section
      id="editorial"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="editorial-section"
    >
      <div ref={ref} className="reveal max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-6">
          <div className="font-mono-label text-[var(--lab-orange)] mb-6">
            {t.editorial.tag}
          </div>
          <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-[var(--lab-ink)] mb-8">
            {t.editorial.title1}
            <br />
            <span className="font-serif-italic text-[var(--lab-ink)]">{t.editorial.title2}</span>
          </h2>
          <p className="font-serif-italic text-xl text-[var(--lab-ink)]/70 leading-relaxed mb-12 max-w-lg">
            {t.editorial.subtitle}
          </p>

          <div className="space-y-2" id="services">
            {t.editorial.items.map((it) => (
              <div
                key={it.n}
                className="group flex items-start gap-6 py-6 border-t border-[var(--lab-line)] hover:pl-2 transition-all duration-300"
                data-testid={`editorial-item-${it.n}`}
              >
                <div className="font-display text-3xl text-[var(--lab-orange)]">
                  {it.n}
                </div>
                <div className="flex-1">
                  <h3 className="font-mono-label text-[var(--lab-ink)] mb-2">
                    {it.title}
                  </h3>
                  <p className="text-[var(--lab-ink)]/70 text-sm leading-relaxed">
                    {it.desc}
                  </p>
                </div>
                <ArrowUpRight
                  size={20}
                  className="text-[var(--lab-ink)]/30 group-hover:text-[var(--lab-orange)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-6 grid grid-cols-2 gap-6 md:pt-20">
          <div className="aspect-[3/4] overflow-hidden rounded-md bg-[var(--lab-cream-2)] transform md:-rotate-2 hover:rotate-0 transition-transform duration-500">
            <img
              src={BOOK_IMG}
              alt="Editorial design sample"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="aspect-[3/4] mt-12 rounded-md overflow-hidden bg-[var(--lab-ink)] p-6 flex flex-col justify-between transform md:rotate-2 hover:rotate-0 transition-transform duration-500 relative">
            <div className="flex justify-between items-start">
              <div className="bg-[var(--lab-orange)] text-white text-xs font-bold px-2 py-1 rounded">2M</div>
              <div className="font-mono-label text-white/60 text-[0.6rem]">EDITORIAL</div>
            </div>
            <div>
              <div className="font-display text-2xl text-white leading-tight">
                MOGAZINE
              </div>
              <div className="font-serif-italic text-white/80 text-2xl mt-1">
                Pacoett
              </div>
            </div>
            <div className="absolute bottom-16 right-0 w-32 h-32 rounded-full bg-[#6b8b96] opacity-90 -mr-8" />
            <div className="absolute bottom-6 right-0 left-0 h-8 bg-[var(--lab-orange)]" style={{ clipPath: "polygon(0 100%, 100% 0, 100% 100%)" }} />
            <div className="font-mono-label text-white/70 text-[0.6rem] relative z-10 mt-4">
              RESITVIED
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- CTA ----------
const CTA = ({ onOpenContact }) => {
  const { t } = useLang();
  const ref = useReveal();

  return (
    <section
      className="relative bg-[var(--lab-orange)] py-24 md:py-32 overflow-hidden"
      data-testid="cta-section"
    >
      <div className="grain-overlay opacity-25" />
      <div
        ref={ref}
        className="reveal max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center relative"
      >
        <div className="md:col-span-7">
          <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.95]">
            {t.cta.title1}
            <br />
            <span className="font-serif-italic text-white/95">{t.cta.title2}</span>
          </h2>
        </div>
        <div className="md:col-span-5 space-y-8">
          <p className="text-white/90 text-lg leading-relaxed max-w-md">
            {t.cta.description}
          </p>
          <button
            onClick={onOpenContact}
            data-testid="cta-contact-btn"
            className="btn-pill bg-[var(--lab-ink)] text-white hover:bg-white hover:text-[var(--lab-ink)] group"
          >
            {t.cta.button}
            <ArrowRight size={16} className="arrow-anim" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ---------- Footer ----------
const Footer = () => {
  const { t } = useLang();
  return (
    <footer
      className="bg-[var(--lab-cream)] border-t border-[var(--lab-line)] py-10"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 items-center gap-6">
        <div className="flex justify-start">
          <Logo className="h-12 w-auto" />
        </div>
        <div className="text-center">
          <div className="font-mono-label text-[var(--lab-ink)]/70 mb-1">
            {t.footer.subtitle}
          </div>
          <a
            href={`mailto:${t.footer.email}`}
            className="text-[var(--lab-ink)] hover:text-[var(--lab-orange)] transition-colors text-sm"
            data-testid="footer-email"
          >
            {t.footer.email}
          </a>
        </div>
        <div className="text-right font-mono-label text-[var(--lab-ink)]/60 text-xs">
          {t.footer.copyright}
        </div>
      </div>
    </footer>
  );
};

// ---------- Page ----------
export default function Landing() {
  const [contactOpen, setContactOpen] = useState(false);
  const openContact = () => setContactOpen(true);

  return (
    <div className="min-h-screen bg-[var(--lab-cream)] text-[var(--lab-ink)] relative">
      <Header onOpenContact={openContact} />
      <Hero onOpenContact={openContact} />

      <Products onOpenContact={openContact} />
      <Editorial />
      <CTA onOpenContact={openContact} />
      <Footer />
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
